import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Mail, MessageSquare, Phone, Search, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Colleague,
  ColleagueDetail,
  ColleagueFilters,
  ConversationSummary,
} from '@up2d8/contracts';
import { Avatar, SectionHead, cn } from '@up2d8/ui';
import { useSession } from '../../../shared/session';
import { shiftFilter, type ColleagueFilter } from '../../../shared/colleague-filter';

/**
 * Het collegascherm op een bureauscherm (collega's-replica-spec).
 *
 * Twee kolommen: de lijst en de profielkaart van wie je aanklikt. De lijst kent vijf kolommen
 * waarvan er twee pas bestaan als het bedrijf iets zichtbaar zette (SCH-3) — is er niets, dan
 * verdwijnt de kolom in plaats van een rij streepjes te tonen.
 *
 * **Geen online-stip, en ook niet de ruimte ervoor** (besluit van 6 oktober, ADR-0068). Aanwezigheid
 * is een feit over een persoon; dat hoort hier net zo min als in een gesprek. De regel "Online"
 * onder de naam in de profielkaart valt om dezelfde reden weg.
 */
export interface DeskColleaguesProps {
  search: string;
  onSearch: (value: string) => void;
  /** De gekozen filterknop; de pagina vraagt de lijst daarmee aan de server (§2.2). */
  filter: ColleagueFilter;
  onFilter: (value: ColleagueFilter) => void;
  /** De collega die het globale zoeken opende; zijn profielkaart staat meteen open. */
  openPersonId?: string | null;
  items: Colleague[];
  total: number;
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onMore: () => void;
}

export function DeskColleagues({
  search,
  onSearch,
  filter,
  onFilter,
  openPersonId = null,
  items,
  total,
  loading,
  hasMore,
  loadingMore,
  onMore,
}: DeskColleaguesProps) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const [selected, setSelected] = useState<string | null>(openPersonId);
  // Een tweede zoekopdracht terwijl dit scherm al openstaat, opent die collega.
  useEffect(() => {
    if (openPersonId) setSelected(openPersonId);
  }, [openPersonId]);

  const filters = useQuery({
    queryKey: ['colleagues', 'filters'],
    queryFn: async (): Promise<ColleagueFilters> => {
      const { data, error } = await api.GET('/api/v1/colleagues/filters', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ColleagueFilters;
    },
  });

  /*
    De filters vraagt de pagina aan de server (§2.2): `items` en `total` zijn al gefilterd. Eerst
    filterde dit scherm de geladen pagina, en dan vond "Productie" alleen wie op de eerste pagina
    stond (testrapport 28 okt., #17).
  */
  const knoppen: { key: string | null; label: string }[] = [
    { key: null, label: t('colleagues.filterAll') },
    ...(filters.data?.myShift
      ? [{ key: shiftFilter(filters.data.myShift), label: t('colleagues.filterMyShift') }]
      : []),
    ...(filters.data?.departments ?? []).map((naam) => ({ key: naam, label: naam })),
  ];

  return (
    <div
      data-testid="colleagues-layout"
      className="grid w-full grid-cols-[minmax(0,1fr)] gap-4 desk:grid-cols-[minmax(0,1fr)_280px] wide:gap-5 wide:grid-cols-[minmax(0,1fr)_320px]"
    >
      <div className="min-w-0">
        <SectionHead
          title={t('colleagues.title')}
          action={
            <span className="rounded-pill bg-brand-dark-blue-tint px-2 py-0.5 text-micro text-brand-dark-blue">
              {total}
            </span>
          }
        />

        {/* §2.2 — eerst zoeken, dan filteren. */}
        <div className="relative mb-3">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-[13px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text-muted"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            data-testid="colleague-search-desk"
            /*
              Een eigen label, en niet hetzelfde als op de telefoon.

              Beide indelingen staan in dezelfde DOM (de ene verborgen met CSS). Twee invoervelden
              met exact hetzelfde label op een pagina is verwarrend voor een schermlezer en
              dubbelzinnig voor elke test die erop zoekt. Dezelfde oplossing als bij het
              berichtenscherm in BER-5.
            */
            aria-label={t('colleagues.searchDesk')}
            placeholder={t('colleagues.searchPlaceholderDesk')}
            className="h-[38px] w-full rounded-pill border border-border bg-background pl-[38px] pr-[14px] text-small text-text-primary outline-none placeholder:text-text-muted focus:border-brand-primary focus:bg-surface focus:shadow-[0_0_0_4px_rgba(0,178,221,0.1)]"
          />
        </div>

        <div className="mb-3 flex flex-wrap gap-1.5">
          {knoppen.map((knop) => (
            <button
              key={knop.key ?? 'alle'}
              type="button"
              data-testid="colleague-filter"
              aria-pressed={filter === knop.key}
              onClick={() => onFilter(knop.key)}
              className={cn(
                'rounded-pill px-3 py-1.5 font-heading text-caption font-medium transition-colors',
                filter === knop.key
                  ? 'bg-brand-primary-tint font-semibold text-brand-primary-dark'
                  : 'text-text-secondary hover:bg-background hover:text-brand-primary',
              )}
            >
              {knop.label}
            </button>
          ))}
        </div>

        {/* §2.3 — de lijstkaart. */}
        <div className="overflow-hidden rounded-card border border-border-light bg-surface shadow-sm">
          <div className="grid grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1.2fr)_72px] items-center gap-3 border-b border-border-light px-[22px] pb-2.5 pt-3.5 font-heading text-micro font-medium uppercase tracking-[0.1em] text-text-muted">
            <span>{t('colleagues.columnName')}</span>
            <span>{t('colleagues.columnDepartment')}</span>
            <span>{t('colleagues.columnShift')}</span>
            <span />
          </div>

          <div className="flex flex-col gap-0.5 px-2 pb-2 pt-1">
            {loading ? (
              <p className="px-3.5 py-4 text-small text-text-muted">{t('common.loading')}</p>
            ) : items.length === 0 ? (
              <p
                data-testid="colleagues-empty-desk"
                className="px-3.5 py-4 text-small text-text-muted"
              >
                {t('colleagues.empty')}
              </p>
            ) : (
              items.map((colleague, index) => {
                // Het letterlabel staat op de **voornaam**, en de server sorteert daar ook op.
                // Zouden die twee verschillen, dan staat er een "J" boven Lieselot.
                const letter = colleague.displayName.charAt(0).toUpperCase();
                const vorige = items[index - 1]?.displayName.charAt(0).toUpperCase();

                return (
                  <div key={colleague.personId}>
                    {letter !== vorige && (
                      <p className="px-3.5 pb-1 pt-2.5 font-heading text-micro font-medium uppercase tracking-[0.1em] text-text-muted">
                        {letter}
                      </p>
                    )}
                    <button
                      type="button"
                      data-testid="colleague-row"
                      aria-pressed={selected === colleague.personId}
                      onClick={() => setSelected(colleague.personId)}
                      className={cn(
                        'grid w-full grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1.2fr)_72px] items-center gap-3 rounded-md px-3.5 py-2.5 text-left transition-colors',
                        selected === colleague.personId
                          ? 'bg-brand-primary-tint'
                          : 'hover:bg-background',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <Avatar name={colleague.displayName} size="lg" />
                        <span className="min-w-0 truncate font-heading text-small font-semibold text-text-primary">
                          {colleague.displayName}
                        </span>
                      </span>
                      <span className="min-w-0">
                        {colleague.department ? (
                          <span className="inline-block max-w-full truncate rounded-pill bg-brand-primary-tint px-2 py-px font-heading text-micro font-semibold text-brand-primary-dark">
                            {colleague.department}
                          </span>
                        ) : null}
                      </span>
                      <span className="min-w-0 truncate text-caption text-text-secondary">
                        {colleague.shift ?? ''}
                      </span>
                      <span className="flex justify-end">
                        {has('messaging.conversation.use') && (
                          <span
                            aria-hidden
                            className="flex h-8 w-8 items-center justify-center rounded-pill text-text-muted"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </span>
                        )}
                      </span>
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* §2.3 — de voet. Het getal is geteld, niet geschat. */}
          <div className="flex items-center justify-between gap-3 border-t border-border-light px-[22px] py-3">
            <span className="text-caption text-text-muted" data-testid="colleague-count">
              {t('colleagues.shownOf', { shown: items.length, total })}
            </span>
            {hasMore && (
              <button
                type="button"
                onClick={onMore}
                disabled={loadingMore}
                data-testid="colleague-more"
                className="rounded-pill border border-border-light px-3.5 py-[7px] font-heading text-caption font-medium text-text-secondary transition-colors hover:border-brand-primary hover:text-brand-primary disabled:opacity-60"
              >
                {t('colleagues.more')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* §3 — de profielkaart. Vanaf het bureauscherm (900 px) en niet pas vanaf 1100 px: de
          rij heeft geen eigen berichtknop, dus zonder kaart kun je daartussen niemand bereiken.
          Tot 1100 px een smallere kolom, zoals paneel 1 op het berichtenscherm. */}
      <div className="hidden min-w-0 desk:block">
        {selected ? (
          <ColleagueCard personId={selected} />
        ) : (
          <div className="rounded-card border border-border-light bg-surface p-element text-center shadow-sm">
            <Users aria-hidden className="mx-auto mb-2 h-7 w-7 text-text-muted" />
            <p className="font-heading text-content font-semibold text-text-primary">
              {t('colleagues.pickSomeone')}
            </p>
            <p className="mt-1 text-small text-text-muted">{t('colleagues.pickSomeoneHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * De profielkaart van één collega (§3).
 *
 * Alles wat erop staat komt van `GET /colleagues/:id` — en dat levert alleen wat het bedrijf
 * zichtbaar zette (SCH-3). Een bedrijf dat niets instelde, krijgt een kaart met naam, afdeling en
 * ploeg: dat is de **normale** toestand, geen lege toestand.
 */
function ColleagueCard({ personId }: { personId: string }) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  /**
   * "Bericht" begint een gesprek en opent het (SCH-4).
   *
   * Idempotent aan de serverkant: bestaat het gesprek al, dan open je het bestaande (ADR-0067).
   * Deze knop mag niet enkel navigeren naar het scherm waar je al staat — een tegel die niets doet,
   * hoort er niet te zijn.
   */
  const startConversation = useMutation({
    mutationFn: async (): Promise<ConversationSummary> => {
      const { data, error } = await api.POST('/api/v1/conversations', {
        body: { personId } as never,
      });
      if (error || !data) throw error ?? new Error('Starten mislukt');
      return data as ConversationSummary;
    },
    onSuccess: (conversation) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void navigate({
        to: '/app/gesprekken/$conversationId',
        params: { conversationId: conversation.id },
      });
    },
  });

  const colleague = useQuery({
    queryKey: ['colleagues', 'detail', personId],
    queryFn: async (): Promise<ColleagueDetail> => {
      const { data, error } = await api.GET('/api/v1/colleagues/{personId}', {
        params: { path: { personId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ColleagueDetail;
    },
  });

  const person = colleague.data;
  if (!person) {
    return (
      <div className="rounded-card border border-border-light bg-surface p-element shadow-sm">
        <p className="text-small text-text-muted">{t('common.loading')}</p>
      </div>
    );
  }

  // Een toestelnummer is een veld; of het bestaat, beslist het bedrijf. Zonder nummer geen belknop.
  const telefoon = person.fields.find((veld) => /toestel|telefoon|phone/i.test(veld.key))?.value;

  return (
    <div
      data-testid="colleague-card"
      className="rounded-card border border-border-light bg-surface px-[18px] pb-[18px] pt-5 shadow-sm"
    >
      <div className="flex flex-col items-center text-center">
        <Avatar name={person.displayName} size="xl" className="h-16 w-16 text-title-md" />
        <p className="mt-3 font-heading text-title-sm font-semibold text-text-primary">
          {person.displayName}
        </p>
        {person.department && (
          <span className="mt-2 rounded-pill bg-brand-primary-tint px-2 py-px font-heading text-micro font-semibold text-brand-primary-dark">
            {person.department}
          </span>
        )}
      </div>

      {/* De actietegels. Elke tegel doet iets, of ze staat er niet (§3). */}
      <div data-tiles className="mt-4 grid grid-cols-3 gap-2">
        {has('messaging.conversation.use') && (
          <CardAction
            testId="card-message"
            icon={<MessageSquare aria-hidden className="h-[18px] w-[18px]" />}
            label={t('colleagues.actionMessage')}
            onClick={() => startConversation.mutate()}
          />
        )}
        {telefoon && (
          <CardAction
            testId="card-call"
            icon={<Phone aria-hidden className="h-[18px] w-[18px]" />}
            label={t('colleagues.actionCall')}
            href={`tel:${telefoon}`}
          />
        )}
        {person.email && (
          <CardAction
            testId="card-mail"
            icon={<Mail aria-hidden className="h-[18px] w-[18px]" />}
            label={t('colleagues.actionMail')}
            href={`mailto:${person.email}`}
          />
        )}
      </div>

      {/* De sectie "Gegevens" bestaat alleen als er gegevens zijn. Een kop boven leegte is erger
          dan geen kop. */}
      {(person.shift || person.site || person.email || person.fields.length > 0) && (
        <section className="mt-4 border-t border-border-light pt-3.5">
          <SectionHead title={t('colleagues.details')} />
          <dl className="flex flex-col" data-testid="colleague-details">
            {person.shift && <DetailRow label={t('colleagues.columnShift')} value={person.shift} />}
            {person.site && <DetailRow label={t('colleagues.site')} value={person.site} />}
            {person.email && <DetailRow label={t('colleagues.email')} value={person.email} />}
            {person.fields.map((veld) => (
              <DetailRow key={veld.key} label={veld.label} value={veld.value} />
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}

function CardAction({
  icon,
  label,
  onClick,
  href,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  href?: string;
  testId: string;
}) {
  const className =
    'flex flex-col items-center gap-1.5 rounded-md bg-background px-1.5 py-3 font-heading text-micro font-medium text-text-secondary transition-colors hover:bg-brand-primary-tint hover:text-brand-primary';

  if (href) {
    return (
      <a href={href} data-testid={testId} className={className}>
        {icon}
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={className}>
      {icon}
      {label}
    </button>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <dt className="shrink-0 text-micro text-text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right font-heading text-caption font-medium text-text-primary">
        {value}
      </dd>
    </div>
  );
}
